import { apiClient } from "@/lib/api-client";
import type {
  FoodItem,
  FoodFilters,
  FoodItemInput,
  PaginatedResponse,
  FoodConditionRule,
} from "@/types/nutrition";

const BASE_URL = "nutrition/foods";

export const foodsApi = {
  list: async (
    filters?: FoodFilters,
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResponse<FoodItem>> => {
    const params: Record<string, unknown> = {
      page,
      page_size: pageSize,
    };

    if (filters?.search) params.search = filters.search;
    if (filters?.category) params.category = filters.category;
    if (filters?.categories?.length) params.category = filters.categories.join(",");
    if (filters?.tags?.length) params.food_tags = filters.tags.join(",");
    if (filters?.is_verified !== undefined)
      params.is_verified = filters.is_verified;
    if (filters?.is_global !== undefined) params.is_global = filters.is_global;

    const { data } = await apiClient.get<PaginatedResponse<FoodItem> | FoodItem[]>(BASE_URL, {
      params,
    });

    // Handle both paginated and non-paginated responses
    if (Array.isArray(data)) {
      return {
        count: data.length,
        next: null,
        previous: null,
        results: data,
      };
    }
    const record = data as PaginatedResponse<FoodItem> & { foods?: FoodItem[] };
    const results = record.results ?? record.foods ?? [];
    return {
      ...record,
      results,
      count: record.count ?? results.length,
      next: record.next ?? null,
      previous: record.previous ?? null,
    };
  },

  get: async (id: number): Promise<FoodItem> => {
    const { data } = await apiClient.get<FoodItem>(`${BASE_URL}/${id}`);
    return data;
  },

  create: async (input: FoodItemInput): Promise<FoodItem> => {
    const { data } = await apiClient.post<FoodItem>(BASE_URL, input);
    return data;
  },

  bulkCreate: async (items: FoodItemInput[]): Promise<{
    created_count: number;
    created_ids: number[];
    updated_count: number;
    updated_ids: number[];
    errors: Array<{ index: number; error: string }>;
  }> => {
    const { data } = await apiClient.post<{
      created_count: number;
      created_ids: number[];
      updated_count: number;
      updated_ids: number[];
      errors: Array<{ index: number; error: string }>;
    }>(`${BASE_URL}/bulk`, items);
    return data;
  },

  update: async (id: number, input: Partial<FoodItemInput>): Promise<FoodItem> => {
    const { data } = await apiClient.patch<FoodItem>(`${BASE_URL}/${id}`, input);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  getTags: async (id: number): Promise<FoodItem["food_tags"]> => {
    const { data } = await apiClient.get<FoodItem["food_tags"]>(
      `${BASE_URL}/${id}/tags`
    );
    return data;
  },

  addTags: async (id: number, tagIds: number[]): Promise<FoodItem> => {
    const { data } = await apiClient.post<FoodItem>(`${BASE_URL}/${id}/add_tags`, {
      tag_ids: tagIds,
    });
    return data;
  },

  removeTags: async (id: number, tagIds: number[]): Promise<FoodItem> => {
    const { data } = await apiClient.post<FoodItem>(
      `${BASE_URL}/${id}/remove_tags`,
      { tag_ids: tagIds }
    );
    return data;
  },

  getByTags: async (
    tags: string[],
    matchAll = true
  ): Promise<PaginatedResponse<FoodItem>> => {
    const { data } = await apiClient.get<PaginatedResponse<FoodItem>>(
      `${BASE_URL}/by_tags`,
      {
        params: { tags: tags.join(","), match_all: matchAll },
      }
    );
    return data;
  },

  getConditionRules: async (id: number): Promise<FoodConditionRule[]> => {
    const { data } = await apiClient.get<FoodConditionRule[]>(
      `${BASE_URL}/${id}/condition_rules`
    );
    return data;
  },

  getRecommendationsForConditions: async (
    conditionCodes: string[]
  ): Promise<FoodItem[]> => {
    const { data } = await apiClient.post<FoodItem[]>(
      `${BASE_URL}/recommendations_for_conditions`,
      { condition_codes: conditionCodes }
    );
    return data;
  },

  getAvoidancesForConditions: async (
    conditionCodes: string[],
    includeAlternatives = true
  ): Promise<FoodItem[]> => {
    const { data } = await apiClient.post<FoodItem[]>(
      `${BASE_URL}/avoidances_for_conditions`,
      { condition_codes: conditionCodes, include_alternatives: includeAlternatives }
    );
    return data;
  },

  searchByNutrients: async (params: {
    min_protein?: number;
    max_calories?: number;
    min_fiber?: number;
    max_carbs?: number;
  }): Promise<PaginatedResponse<FoodItem>> => {
    const { data } = await apiClient.get<PaginatedResponse<FoodItem>>(
      `${BASE_URL}/search_by_nutrients`,
      { params }
    );
    return data;
  },
};
